import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDefaultSetItemRestSec } from '../../../hooks/use-default-set-item-rest-sec';
import { useGetAvailableExercises } from '../../../redux/api/exercisesAPI';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { uniqBy } from '../../../utils/collections/array-utils';
import {
  getEmptyProgramSet,
  getEmptyTrainingSet,
  getSetFromSavedProgramSet,
} from '../../../utils/training/set-utils';
import { IUseSetGroupEditTable, SetGroupEditTableMode } from './interfaces';

export function useSetGroupEditComponent<T extends IProgramSet | ITrainingSet>(
  useSetGroupEditTable: IUseSetGroupEditTable<T>,
  sets: T[],
) {
  const { mode } = useSetGroupEditTable;
  const { onAddSet } = useSetGroupEditTable;
  const { data: availableExercises } = useGetAvailableExercises();
  const exercises = availableExercises || [];
  const { defaultRestForNewEmptyItem } = useDefaultSetItemRestSec();
  const [expanded, setExpanded] = useState(true);
  const [isSuperset, setIsSuperset] = useState<boolean>(sets?.length > 1);
  const [showSavedProgramItemModal, setShowSavedProgramItemModal] =
    useState(false);

  useEffect(() => {
    setIsSuperset(sets?.length > 1);
  }, [sets]);

  const onAddSetClick = useCallback(() => {
    const currentSetNumber = sets[0].setNumber;
    const maxOrderNumber = Math.max(...sets.map((item) => item.orderNumber));

    let newSet;
    if (mode === SetGroupEditTableMode.program) {
      const set = sets[0] as IProgramSet;
      newSet = getEmptyProgramSet(currentSetNumber, maxOrderNumber + 1, {
        haveOwner: set.ownerId !== null,
        ownerId: set.ownerId,
        programItemId: set.programItemId,
        defaultItemRestSec: defaultRestForNewEmptyItem,
      });
    } else {
      const set = sets[0] as ITrainingSet;

      newSet = getEmptyTrainingSet(
        currentSetNumber,
        maxOrderNumber + 1,
        set.trainingId,
        defaultRestForNewEmptyItem,
      );
    }

    onAddSet(newSet);
  }, [sets, defaultRestForNewEmptyItem]);

  function onAddSavedProgramSet(savedSet: IProgramSet) {
    const maxOrderNumber = Math.max(...sets.map((s) => s.orderNumber));
    const setWithMaxOrderNumber = sets.find(
      (s) => s.orderNumber === maxOrderNumber,
    );

    const newSet = getSetFromSavedProgramSet(mode, savedSet, {
      setNumber: setWithMaxOrderNumber.setNumber,
      orderNumber: setWithMaxOrderNumber.orderNumber + 1,
      trainingId: (setWithMaxOrderNumber as ITrainingSet).trainingId,
      programItemId: (setWithMaxOrderNumber as IProgramSet).programItemId,
      ownerId: (setWithMaxOrderNumber as IProgramSet).ownerId,
    });

    if (mode === SetGroupEditTableMode.program) {
      if ((setWithMaxOrderNumber as IProgramSet).title) {
        (newSet as IProgramSet).title = (
          setWithMaxOrderNumber as IProgramSet
        ).title;
      }

      if ((setWithMaxOrderNumber as IProgramSet).description) {
        (newSet as IProgramSet).description = (
          setWithMaxOrderNumber as IProgramSet
        ).description;
      }
    }

    onAddSet(newSet as T);
  }

  return {
    isSuperset,
    setIsSuperset,
    exercises,
    expanded,
    setExpanded,
    onAddSetClick,
    onAddSavedProgramSet,
    showSavedProgramItemModal,
    setShowSavedProgramItemModal,
  };
}
